/**
 * §A.5 · Los interruptores, vistos de arriba.
 *
 * ## Lo que esta pantalla contesta y la de la ficha no
 *
 * «A cuántos clubes está sacada esta función ahora mismo.» El contrato lo dice del
 * propio DTO: `overrides` son los clubes con una decisión propia, «y es lo que dice a
 * cuántos está sacada ahora mismo una función en pruebas».
 *
 * Un interruptor que solo existe por club **también sale**, aunque no tenga fila
 * global: es justo el caso de la función que está en pruebas con cinco clubes y no se
 * ha sacado a nadie más.
 *
 * ## Aquí no se puede cambiar nada, y es del contrato
 *
 * §A.5 da **una** ruta de lectura para los globales; el `PUT` es siempre por club.
 * Eso no es una carencia: encender algo «para todos» desde una pantalla es
 * exactamente el gesto que rompe la aplicación a doscientos clubes a la vez el día
 * que `academies.settings` cambie de forma. Se saca club a club, desde su ficha, y
 * esta pantalla es la que dice por dónde va.
 *
 * Se explica en la pantalla, porque una lista sin botones invita a buscar el botón.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { cuenta } from '@/shared/ui/formato';
import {
  nombreDeInterruptor,
  type InterruptorGlobal,
} from '@/backoffice/tipos';

export function InterruptoresGlobales({
  alAbrirClub,
}: {
  alAbrirClub: (clubId: string) => void;
}) {
  const flags = useQuery({
    queryKey: ['admin', 'flags', 'globales'],
    queryFn: async () => {
      const { data, error } = await api.v1.admin['feature-flags'].get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as InterruptorGlobal[];
    },
  });

  if (flags.isPending) return <Cargando filas={4} etiqueta="Cargando" />;

  if (flags.isError || !flags.data) {
    return (
      <PanelError
        problema={aProblema(flags.error)}
        reintentar={() => void flags.refetch()}
      />
    );
  }

  return (
    <section>
      <h1>Interruptores</h1>

      <p className="tenue">
        Qué hay puesto para todos y qué clubes llevan una decisión propia.{' '}
        <strong>Desde aquí no se cambia nada</strong>: se enciende club a club,
        en su ficha. Sacar una función a cinco antes que a doscientos no es
        prudencia excesiva.
      </p>

      {flags.data.length === 0 ? (
        <Vacio
          titulo="No hay ningún interruptor"
          explicacion="Se crean escribiendo su nombre en la ficha de un club. Mientras no haya ninguno, toda la aplicación va con lo que trae de serie."
        />
      ) : (
        <ul className="papeles">
          {flags.data.map((f) => {
            const encendidos = f.overrides.filter((o) => o.enabled);
            const apagados = f.overrides.filter((o) => !o.enabled);
            return (
              <li key={f.flag}>
                <div className="aviso-cabeza">
                  <strong>{nombreDeInterruptor(f.flag)}</strong>
                  <span
                    className={`pastilla ${
                      f.enabled_globally ? 'pastilla-bien' : 'pastilla-paused'
                    } pastilla-left`}
                  >
                    {f.enabled_globally
                      ? 'Encendido para todos'
                      : 'Apagado para todos'}
                  </span>
                  <code className="tenue">{f.flag}</code>
                </div>

                {f.note ? <p>{f.note}</p> : <p className="tenue">Sin nota.</p>}

                {f.overrides.length === 0 ? (
                  <p className="tenue">
                    Ningún club se sale de lo puesto para todos.
                  </p>
                ) : (
                  <>
                    <p>
                      {/*
                        La cifra que se viene a buscar: a cuántos está sacada. Con
                        la concordancia escrita a mano, que interpolar un contador
                        delante de un verbo es donde se cuela «1 clubes».
                      */}
                      {encendidos.length > 0 &&
                        `${cuenta(encendidos.length, 'club', 'clubes')} lo ${
                          encendidos.length === 1 ? 'tiene' : 'tienen'
                        } encendido aparte`}
                      {encendidos.length > 0 && apagados.length > 0 && ' · '}
                      {apagados.length > 0 &&
                        `${cuenta(apagados.length, 'club', 'clubes')} lo ${
                          apagados.length === 1 ? 'tiene' : 'tienen'
                        } apagado aparte`}
                      .
                    </p>

                    <ul className="lista-clara">
                      {f.overrides.map((o) => (
                        <li key={o.academy_id}>
                          <button
                            type="button"
                            className="enlace-fila"
                            onClick={() => alAbrirClub(o.academy_id)}
                          >
                            {o.academy_name}
                          </button>{' '}
                          <span className="tenue">
                            {o.enabled ? 'encendido' : 'apagado'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
